import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export const PLAN_TIERS = {
  starter: { product_id: "prod_UI57dcxW4UQVsM", price_monthly: "price_1TJUxUBr2hTQtpzM1OGxYSAW", price_yearly: "price_1TJUxpBr2hTQtpzMcWu4QpMK" },
  creator: { product_id: "prod_RzS5N3S1aBjS63", price_monthly: "price_1T6Q64Br2hTQtpzMwOESsbBM", price_yearly: "price_1T6Q6GBr2hTQtpzMJ9NwZ1MQ" },
  agency: { product_id: "prod_RzS5lmMz4hR8h3", price_monthly: "price_1T6Q6RBr2hTQtpzM9CGuQeK8", price_yearly: "price_1T6Q6aBr2hTQtpzMq0Pl5uhQ" },
} as const;

export type PlanTier = "free" | "starter" | "creator" | "agency" | "trial";

export function getPlanTier(productId: string | null | undefined): PlanTier {
  if (!productId) return "free";
  if (productId === "free_trial") return "trial";
  if (productId === PLAN_TIERS.starter.product_id) return "starter";
  if (productId === PLAN_TIERS.agency.product_id) return "agency";
  // Default paid to creator
  return "creator";
}

export interface SubscriptionState {
  subscribed: boolean;
  product_id: string | null;
  subscription_end: string | null;
  trial_end: string | null;
  is_trial: boolean;
  trial_expired?: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  subscription: SubscriptionState | null;
  subscriptionLoading: boolean;
  refreshSubscription: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<{
    error: Error | null;
    user: User | null;
    requiresEmailConfirmation: boolean;
    isExistingAccount: boolean;
  }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const DEFAULT_SUB: SubscriptionState = {
  subscribed: false,
  product_id: null,
  subscription_end: null,
  trial_end: null,
  is_trial: false,
};

const getDisplayNameFromUser = (user: User) => {
  const fullName = user.user_metadata?.full_name;
  if (typeof fullName === "string" && fullName.trim()) return fullName.trim();
  return user.email?.split("@")[0] ?? null;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionState | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const pendingCheckoutRunning = useRef(false);

  const resumePendingCheckout = useCallback(async () => {
    const raw = localStorage.getItem("pending_subscription_checkout");
    if (!raw || pendingCheckoutRunning.current) return;

    pendingCheckoutRunning.current = true;
    try {
      const pending = JSON.parse(raw) as {
        priceId?: string;
        mode?: string;
        successUrl?: string;
        cancelUrl?: string;
        couponId?: string;
      };
      if (!pending.priceId) {
        localStorage.removeItem("pending_subscription_checkout");
        return;
      }

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: pending,
      });
      if (error || !data?.url) return;

      localStorage.removeItem("pending_subscription_checkout");
      window.location.assign(data.url as string);
    } catch {
      localStorage.removeItem("pending_subscription_checkout");
    } finally {
      pendingCheckoutRunning.current = false;
    }
  }, []);

  const checkSubscription = useCallback(async () => {
    setSubscriptionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) throw error;
      setSubscription(data as SubscriptionState);
    } catch {
      setSubscription(DEFAULT_SUB);
    } finally {
      setSubscriptionLoading(false);
    }
  }, []);

  const ensureProfile = useCallback(async (currentUser: User) => {
    const { error } = await supabase
      .from("profiles")
      .upsert(
        {
          user_id: currentUser.id,
          display_name: getDisplayNameFromUser(currentUser),
        },
        {
          onConflict: "user_id",
          ignoreDuplicates: true,
        },
      );

    if (error) {
      console.log("Profile bootstrap failed:", error);
    }
  }, []);

  useEffect(() => {
    // Listen first to avoid missing the initial auth event during session restore
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user) {
        void ensureProfile(session.user);
        void checkSubscription();
        void resumePendingCheckout();
      } else {
        setSubscription(null);
      }

      // Process pending referral after OAuth signup
      if (event === "SIGNED_IN" && session?.user) {
        const pendingRef = localStorage.getItem("pending_referral");
        if (pendingRef) {
          void supabase.functions
            .invoke("save-referral", { body: { referral_code: pendingRef } })
            .then(({ error }) => {
              // Only drop the stored code once the backend accepted (or definitively rejected) it,
              // so a transient failure doesn't silently lose the referral.
              if (!error) localStorage.removeItem("pending_referral");
            })
            .catch((e) => console.log("Pending referral save failed:", e));
        }
      }
    });

    // Then restore any existing session from storage
    void supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        void ensureProfile(session.user);
        void checkSubscription();
        void resumePendingCheckout();
      }
    });

    return () => authSub.unsubscribe();
  }, [checkSubscription, ensureProfile, resumePendingCheckout]);

  // Auto-refresh subscription every 60 seconds while logged in
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(checkSubscription, 60000);
    return () => clearInterval(interval);
  }, [user, checkSubscription]);

  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin },
    });

    const isExistingAccount = !error && !data.session && (data.user?.identities?.length ?? 0) === 0;
    const requiresEmailConfirmation = !error && !data.session && !isExistingAccount;

    return {
      error: error as Error | null,
      user: data.user ?? null,
      requiresEmailConfirmation,
      isExistingAccount,
    };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setSubscription(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, subscription, subscriptionLoading, refreshSubscription: checkSubscription, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
